import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { Box, Drawer, IconButton, List, Button } from "@mui/material";
import { CloseRounded, MenuRounded, HomeRounded } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { WinDims } from "../../hooks/Method";
import { useAppContext } from "context";
import { NavMenu } from "../../components/body";
import CompanyProfile from "../../components/body/CompanyProfile";
import { buildMenu } from "../../components/body/menuConfig";
import { pushRecent } from "../../utils/navPrefs";
import GlobalSearch from "./GlobalSearch";
import NavShortcuts from "./NavShortcuts";
import ModernHeaderActions from "./ModernHeaderActions";

const AppBar = styled(MuiAppBar)(() => ({
  top: 0,
  border: "none",
  borderRadius: 0,
  boxShadow: "var(--shadow-sm)",
  backgroundColor: "var(--white-color)",
  color: "var(--black-color)",
}));

const LG = 1024;

/**
 * Two-tier NetSuite-style shell for the modern UI template.
 *  Tier 1 (light): logo · global search · tools · user
 *  Tier 2 (dark) : Recent/Favorites/Home · main menu
 * Below `lg` it collapses to Tier 1 + a hamburger that opens the classic
 * sidebar (NavMenu). POS hides the nav tier for a full-screen register.
 */
export default function TopNavShell(props) {
  const { menu } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const { access, settings } = useAppContext();
  const { t } = useTranslation("nav");
  const width = WinDims().width;
  const isDesktop = width > LG;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const path = location.pathname || "";
  const inPOS = path === "/pos" || path.includes("/pos/") || path === "/sell" || path.includes("/sell/");
  const showNavTier = isDesktop && !inPOS;

  // Flat menu leaves for resolving the current page → "recent".
  const flat = useMemo(() => {
    const out = [];
    buildMenu(access, settings, t).forEach((g) => {
      if (g.children?.length) g.children.forEach((c) => out.push({ title: c.title, path: c.path }));
      else if (g.path) out.push({ title: g.title, path: g.path });
    });
    return out;
  }, [access, settings, t]);

  // Record the current page into "recent" whenever the route changes.
  useEffect(() => {
    const match = flat.find((f) => path.includes(f.path));
    if (match) pushRecent(match);
  }, [path, flat]);

  // Close the mobile drawer when navigation actually happens (route change) —
  // NOT on every click, so tapping a parent item just expands its children.
  useEffect(() => {
    setDrawerOpen(false);
  }, [path]);

  const bodyMt = showNavTier
    ? "calc(var(--utilitybar-height) + var(--navbar-height))"
    : "var(--utilitybar-height)";

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <AppBar position="fixed">
        {/* ---- Tier 1: utility bar (light) ---- */}
        <div
          className="flex items-center gap-2 w-full px-[12px]"
          style={{ height: "var(--utilitybar-height)" }}
        >
          {!isDesktop && (
            <IconButton onClick={() => setDrawerOpen((o) => !o)} size="small" sx={{ color: "var(--gray-color)" }}>
              {drawerOpen ? <CloseRounded /> : <MenuRounded />}
            </IconButton>
          )}

          {/* Company details (logo + company/outlet) in place of a fixed logo.
              min-w-0 + content-based width: shows the full name when there's
              room and only ellipsises when the bar runs out of space. */}
          <div className="min-w-0 shrink">
            <CompanyProfile light />
          </div>

          {/* In POS the nav tier is hidden, so surface a way back to the dashboard */}
          {inPOS && access?.dashboardAccess && (
            <Button
              onClick={() => navigate("/home")}
              startIcon={<HomeRounded />}
              size="small"
              sx={{
                ms: 1,
                flexShrink: 0,
                color: "var(--primary-color)",
                backgroundColor: "var(--primarySM-color)",
                "&:hover": { backgroundColor: "var(--primaryMD-color)" },
              }}
            >
              {t("home", "Home")}
            </Button>
          )}

          {/* Global search — center, hidden on small screens & POS */}
          {!inPOS && (
            <div className="hidden md:flex flex-1 justify-center px-2">
              <div className="w-full" style={{ maxWidth: 560 }}>
                <GlobalSearch />
              </div>
            </div>
          )}
          {/* Spacer pushes actions to the right when the search is not shown */}
          <Box sx={{ flexGrow: 1, display: inPOS ? "block" : { xs: "block", md: "none" } }} />

          <ModernHeaderActions />
        </div>

        {/* ---- Tier 2: navigation bar (dark) ---- */}
        {showNavTier && (
          <div
            className="flex items-center w-full px-[12px]"
            style={{ height: "var(--navbar-height)", backgroundColor: "var(--primary-color)" }}
          >
            <NavShortcuts />
            <Box sx={{ flexGrow: 1, overflow: "hidden" }}>{menu}</Box>
          </div>
        )}
      </AppBar>

      {/* Mobile navigation drawer reuses the classic sidebar */}
      {!isDesktop && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{ "& .MuiDrawer-paper": { width: 260, backgroundColor: "#e7e5e8", border: "none" } }}
        >
          <List sx={{ pt: "70px", overflow: "auto" }}>
            <NavMenu />
          </List>
        </Drawer>
      )}

      {/* Body */}
      <Box
        component="main"
        sx={{
          p: "var(--page-pad)",
          width: "100%",
          mt: bodyMt,
          height: `calc(100vh - ${bodyMt})`,
          overflow: "auto",
          flexGrow: 1,
        }}
      >
        {props.children}
      </Box>
    </div>
  );
}
